import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Mail, X } from 'lucide-react';
import { usePopupContext } from '../contexts/PopupContext';
import { hero_content } from '../data/content';
import LiquidBlobs from './LiquidBlobs';

const HeroSection = () => {
  const [showResumeModal, setShowResumeModal] = useState(false);
  const { showInfo } = usePopupContext();

  // Use static data instead of API call
  const heroContent = hero_content[0] || null;

  return (
    <>
    {/* Resume Request Modal */}
    {showResumeModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-4 text-text-primary">Request Resume</h3>
            <button 
              onClick={() => setShowResumeModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-4 mb-6">
            <p className="text-text-secondary">
              I'd be happy to share my resume with you. Please use the contact form to send me a message with:
            </p>
            <ul className="text-text-secondary text-sm space-y-2 list-disc list-inside ml-4">
              <li>Your name and company</li>
              <li>Brief description of the opportunity</li>
              <li>Timeline for the role</li>
            </ul>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={() => {
                setShowResumeModal(false);
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                showInfo(
                  'Scrolling to contact section! Please use the form below to share details about the opportunity.',
                  'Resume Request - Contact Form'
                );
              }}
              className="btn-base btn-primary flex-1"
            >
              <Mail size={16} className="mr-2" />
              Contact Me
            </Button>
            <Button 
              onClick={() => setShowResumeModal(false)}
              className="btn-base btn-secondary"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    )}
    
    <section className="hero-section relative min-h-screen flex items-center overflow-hidden bg-bg-page">
      {/* Background Image with Overlay */}
      <div className="hero-background absolute inset-0">
        <img 
          src={heroContent?.background_image || "https://images.unsplash.com/photo-1738566061977-fdd59ca5b08e"}
          alt="Professional Finance Leadership"
          className="hero-image w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0 bg-gradient-to-r from-bg-page via-bg-page/90 to-transparent"></div>
      </div>

      {/* Liquid Blobs */}
      <LiquidBlobs section="hero" />

      {/* Content */}
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl">
          {/* Animated ribbon */}
          <div className="creative-ribbon mb-8 relative">
            <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wider transform rotate-2 animate-pulse shadow-lg">
              {heroContent?.ribbon_text || "AI Innovation Leader"}
            </div>
          </div>

          {/* Main heading */}
          <h1 className="hero-title text-6xl md:text-8xl font-black mb-6 leading-none font-grotesk uppercase text-white bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent" style={{
            textShadow: '0 0 30px rgba(59, 130, 246, 0.5), 0 0 60px rgba(59, 130, 246, 0.3)',
            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.8))',
            backgroundImage: 'linear-gradient(45deg, #ffffff 0%, #dbeafe 25%, #3b82f6 50%, #dbeafe 75%, #ffffff 100%)',
            backgroundSize: '400% 400%',
            animation: 'gradientShift 3s ease-in-out infinite'
          }}>
            {heroContent?.title || "GIDEON METRE"}
          </h1>

          {/* Subtitle */}
          <div className="mb-8 max-w-2xl">
            <p className="body-large text-gray-800 font-semibold px-6 py-4 bg-gray-100/90 backdrop-blur-sm rounded-lg border border-gray-200/50 shadow-lg" style={{fontSize: '1.125rem'}}>
              {heroContent?.subtitle || "Senior Investment Management Leader | AI Implementation Sponsor | Building High-Performance Teams & Driving Innovation in Asset Management"}
            </p>
          </div>

          {/* Key achievements grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="liquid-glass glass-card p-4 rounded-lg border-l-4 border-l-green-500 transition-all duration-300 hover:scale-110 hover:-translate-y-2 hover:shadow-xl cursor-pointer">
              <div className="text-green-600 font-bold text-xl">{heroContent?.achievements?.experience?.value || "~15"}</div>
              <div className="text-text-secondary text-sm">{heroContent?.achievements?.experience?.label || "Years Experience"}</div>
            </div>
            <div className="liquid-glass glass-card p-4 rounded-lg border-l-4 border-l-blue-500 transition-all duration-300 hover:scale-110 hover:-translate-y-2 hover:shadow-xl cursor-pointer">
              <div className="text-blue-600 font-bold text-xl">{heroContent?.achievements?.team?.value || "20+"}</div>
              <div className="text-text-secondary text-sm">{heroContent?.achievements?.team?.label || "Team Members"}</div>
            </div>
            <div className="liquid-glass glass-card p-4 rounded-lg border-l-4 border-l-purple-500 transition-all duration-300 hover:scale-110 hover:-translate-y-2 hover:shadow-xl cursor-pointer">
              <div className="text-purple-600 font-bold text-xl">{heroContent?.achievements?.savings?.value || "4200+"}</div>
              <div className="text-text-secondary text-sm">{heroContent?.achievements?.savings?.label || "FTE Hours Saved"}</div>
            </div>
            <div className="liquid-glass glass-card p-4 rounded-lg border-l-4 border-l-amber-500 transition-all duration-300 hover:scale-110 hover:-translate-y-2 hover:shadow-xl cursor-pointer">
              <div className="text-amber-600 font-bold text-xl">{heroContent?.achievements?.engagement?.value || "98%"}</div>
              <div className="text-text-secondary text-sm">{heroContent?.achievements?.engagement?.label || "Engagement Score"}</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              className="btn-base btn-primary btn-lg group"
              onClick={() => document.getElementById('personal').scrollIntoView({ behavior: 'smooth' })}
            >
              {heroContent?.cta_button_text || "Explore My Journey"}
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              className="btn-base btn-primary btn-lg group"
              onClick={() => setShowResumeModal(true)}
            >
              <Mail size={20} className="mr-2 group-hover:scale-110 transition-transform" />
              {heroContent?.resume_button_text || "Request Resume"}
            </Button>
          </div>
        </div>


      </div>

      {/* Floating elements for visual interest */}
      <div className="absolute top-20 right-10 w-20 h-20 bg-brand-primary/10 rounded-full animate-bounce delay-100"></div>
      <div className="absolute bottom-32 right-32 w-16 h-16 bg-secondary-olive/20 rounded-full animate-pulse delay-300"></div>
      <div className="absolute top-1/2 right-20 w-12 h-12 bg-brand-primary/5 rounded-full animate-ping delay-500"></div>
    </section>
    </>
  );
};

export default HeroSection;
